import React, { useState, useEffect } from 'react';
import { Calculator, Lightbulb, TrendingUp, CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import CustomSelect from '../../ui/CustomSelect';
import apiClient from '../../../api/apiClient';

interface SalarySimulatorProps {
  positions: any[];
  salaryPolicies: any[];
  formatCurrency: (value: number) => string;
}

export default function SalarySimulator({
  positions,
  salaryPolicies,
  formatCurrency: formatCurrencyProp,
}: SalarySimulatorProps) {
  const [selectedPosition, setSelectedPosition] = useState('');
  const [tenureYears, setTenureYears] = useState(0);
  const [competencyLevel, setCompetencyLevel] = useState(3);
  const [simulationResult, setSimulationResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Fallback formatCurrency function
  const formatCurrency = (value: number | null | undefined): string => {
    if (formatCurrencyProp && typeof formatCurrencyProp === 'function') {
      return formatCurrencyProp(value || 0);
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const activePolicy = salaryPolicies.find(p => p.is_active === true || p.is_active === 'true');

  const competencyLevels = {
    1: '1 - Novice',
    2: '2 - Beginner',
    3: '3 - Competent',
    4: '4 - Proficient',
    5: '5 - Expert',
  };

  // Calculate salary when position, tenure, or competency changes
  useEffect(() => {
    const calculateSalary = async () => {
      if (!selectedPosition) {
        setSimulationResult(null);
        return;
      }

      setLoading(true);
      try {
        const response = await apiClient.post('/hris/organization/calculate-salary-simulation', {
          position_id: parseInt(selectedPosition),
          tenure_years: tenureYears,
          competency_level: competencyLevel,
        });
        setSimulationResult(response);
      } catch (error) {
        console.error('Error calculating salary:', error);
        setSimulationResult(null);
      } finally {
        setLoading(false);
      }
    };

    calculateSalary();
  }, [selectedPosition, tenureYears, competencyLevel]);

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calculator className="w-5 h-5" />
          Simulasi Gaji
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {activePolicy && (
          <Alert className="border-emerald-200 bg-emerald-50 rounded-lg">
            <AlertDescription className="text-emerald-800 text-sm">
              Simulasi menggunakan kebijakan:{' '}
              <span className="font-bold">{activePolicy.policy_name}</span>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label>
            Pilih Jabatan <span className="text-red-500">*</span>
          </Label>
          <CustomSelect
            value={selectedPosition}
            onChange={value => setSelectedPosition(value)}
            placeholder="Pilih jabatan"
            width="w-full"
            options={[
              { value: '', label: 'Pilih jabatan' },
              ...positions.map(pos => ({
                value: String(pos.id),
                label: `${pos.name}${pos.level !== null && pos.level !== undefined ? ` (Level ${pos.level})` : ''}`,
              })),
            ]}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Masa Kerja (Tahun)</Label>
            <Input
              type="number"
              min="0"
              max="50"
              value={tenureYears}
              onChange={e => setTenureYears(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="space-y-2">
            <Label>Level Kompetensi</Label>
            <CustomSelect
              value={String(competencyLevel)}
              onChange={value => setCompetencyLevel(parseInt(value))}
              placeholder="Pilih level kompetensi"
              width="w-full"
              options={Object.entries(competencyLevels).map(([value, label]) => ({
                value,
                label,
              }))}
            />
          </div>
        </div>

        {/* Simulation Result */}
        {simulationResult && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg text-gray-900">Hasil Simulasi</h3>
                {simulationResult.is_valid ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-md shadow-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-emerald-600 font-medium text-sm">Valid</span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md shadow-sm">
                    <span className="text-red-600 font-medium text-sm">Invalid</span>
                  </div>
                )}
              </div>

              <div className="space-y-3 text-sm mb-4 bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                <div className="flex justify-between items-center">
                  <p className="text-gray-500 text-base font-medium">Jabatan</p>
                  <p className="font-semibold text-gray-900">
                    {simulationResult.position?.name || '-'}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-gray-500 text-base font-medium">Level Jabatan</p>
                  <div className="px-2 py-1 bg-gray-100 border border-gray-200 rounded text-gray-900">
                    {simulationResult.position?.level || '-'}
                    {simulationResult.job_grade?.grade_level
                      ? ` / ${simulationResult.job_grade.grade_level}`
                      : ''}
                  </div>
                </div>
              </div>

              <div className="space-y-0 text-sm border border-gray-200 rounded-md overflow-hidden">
                {/* Mid Salary (Base) - Light Blue Background */}
                <div className="flex justify-between items-center px-3 py-2 bg-blue-50">
                  <span className="text-gray-700">Mid Salary (Base)</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(simulationResult.calculation?.mid_salary || 0)}
                  </span>
                </div>

                {/* Masa Kerja - White Background */}
                <div className="flex justify-between items-center px-3 py-2 bg-white border-t border-gray-200">
                  <span className="text-gray-600">+ Masa Kerja ({tenureYears} tahun)</span>
                  <span className="font-semibold text-blue-600">
                    {formatCurrency(simulationResult.calculation?.tenure_adjustment || 0)}
                  </span>
                </div>

                {/* Kompetensi - White Background */}
                <div className="flex justify-between items-center px-3 py-2 bg-white border-t border-gray-200">
                  <span className="text-gray-600">+ Kompetensi (Level {competencyLevel})</span>
                  <span className="font-semibold text-purple-600">
                    {formatCurrency(simulationResult.calculation?.competency_adjustment || 0)}
                  </span>
                </div>
              </div>

              {/* Gaji Pokok Simulasi - Light Green Background */}
              <div className="mt-4 px-3 py-2 bg-emerald-50 rounded-md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <span className="font-bold text-gray-900">Gaji Pokok Simulasi</span>
                  </div>
                  <span className="text-lg font-bold text-emerald-600">
                    {formatCurrency(simulationResult.calculation?.calculated_salary || 0)}
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500 mt-2">
                Range Valid: {formatCurrency(simulationResult.range?.min_salary || 0)} -{' '}
                {formatCurrency(simulationResult.range?.max_salary || 0)}
              </div>

              <Alert className="border-yellow-200 bg-yellow-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <AlertDescription className="text-yellow-800 text-xs">
                    Simulasi ini tidak disimpan ke payroll. Gunakan untuk perkiraan saja.
                  </AlertDescription>
                </div>
              </Alert>
            </div>
          </div>
        )}

        {loading && (
          <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
            Menghitung...
          </div>
        )}
      </CardContent>
    </Card>
  );
}
